/**
 * Utilidad de Impresión y Generación de PDF Limpio para RxApp
 * Genera documentos comerciales oficiales (Facturas, Ventas y Cotizaciones)
 * sin interfaz de usuario residual, optimizados para formato Carta / A4.
 */

const formatCOP = (num) => {
  return (num || 0).toLocaleString('es-CO', {
    style: 'currency',
    currency: 'COP',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  });
};

const formatDate = (dateStr) => {
  if (!dateStr) return 'N/A';
  const d = new Date(dateStr);
  return d.toLocaleDateString('es-CO', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
};

/**
 * Genera e imprime el documento comercial oficial de una FACTURA
 */
export const printInvoiceDocument = (invoice) => {
  if (!invoice) return;

  const printWin = window.open('', '_blank', 'width=900,height=750');
  if (!printWin) {
    alert('Por favor permite las ventanas emergentes (popups) para generar el documento de impresión.');
    return;
  }

  const baseUrl = window.location.origin;
  const client = invoice.client || {};
  const totalPaid = invoice.totalPaid || 0;
  const balanceDue = Math.max(0, (invoice.total || 0) - totalPaid);
  const isPaid = invoice.paymentStatus === 'PAID' || balanceDue === 0;

  const statusLabel = isPaid 
    ? 'PAGADA' 
    : (totalPaid > 0 ? 'PAGO PARCIAL' : 'PENDIENTE');

  const statusColor = isPaid ? '#059669' : (totalPaid > 0 ? '#2563EB' : '#D97706');
  const statusBg = isPaid ? '#D1FAE5' : (totalPaid > 0 ? '#DBEAFE' : '#FEF3C7');

  const planType = invoice.paymentPlan?.type === 'CUSTOM'
    ? 'Cuotas Personalizadas'
    : (invoice.paymentPlan?.type || '100% Anticipado');

  // Filas de servicios
  const itemsHtml = (invoice.items || []).map((item, idx) => `
    <tr>
      <td style="text-align: center; color: #6B7280; font-size: 12px;">${idx + 1}</td>
      <td>
        <strong style="color: #111827; font-size: 13px; display: block;">${item.service?.name || item.description || 'Servicio Profesional'}</strong>
        ${item.service?.description && item.service?.description !== item.description ? `<span style="font-size: 11px; color: #6B7280;">${item.service.description}</span>` : ''}
      </td>
      <td style="text-align: center; font-weight: 600; font-size: 13px;">${item.quantity || 1}</td>
      <td style="text-align: right; font-size: 13px;">${formatCOP(item.unitPrice)}</td>
      <td style="text-align: right; font-weight: 700; color: #111827; font-size: 13px;">${formatCOP(item.totalPrice)}</td>
    </tr>
  `).join('');

  // Filas de pagos/abonos realizados
  const paymentsHtml = (invoice.payments && invoice.payments.length > 0) ? `
    <div style="margin-top: 24px;">
      <h3 style="font-size: 13px; text-transform: uppercase; letter-spacing: 0.05em; color: #374151; margin-bottom: 8px; border-bottom: 1px solid #E5E7EB; padding-bottom: 4px;">
        Historial de Abonos Recibidos
      </h3>
      <table style="width: 100%; border-collapse: collapse; font-size: 12px;">
        <thead>
          <tr style="background-color: #F9FAFB; color: #4B5563;">
            <th style="padding: 6px 10px; text-align: left;">Fecha</th>
            <th style="padding: 6px 10px; text-align: left;">Método</th>
            <th style="padding: 6px 10px; text-align: left;">Referencia / Comprobante</th>
            <th style="padding: 6px 10px; text-align: left;">Nota</th>
            <th style="padding: 6px 10px; text-align: right;">Monto</th>
          </tr>
        </thead>
        <tbody>
          ${invoice.payments.map(p => `
            <tr style="border-bottom: 1px solid #F3F4F6;">
              <td style="padding: 6px 10px;">${formatDate(p.date)}</td>
              <td style="padding: 6px 10px; font-weight: 600;">${p.method || 'Transferencia'}</td>
              <td style="padding: 6px 10px; color: #6B7280;">${p.reference || 'N/A'}</td>
              <td style="padding: 6px 10px; color: #6B7280;">${p.note || '-'}</td>
              <td style="padding: 6px 10px; text-align: right; font-weight: 700; color: #059669;">${formatCOP(p.amount)}</td>
            </tr>
          `).join('')}
        </tbody>
      </table>
    </div>
  ` : '';

  printWin.document.write(`
    <!DOCTYPE html>
    <html lang="es">
      <head>
        <meta charset="UTF-8">
        <title>Cuenta de Cobro ${invoice.invoiceNumber || ''} - GRUPO DIGITALXTREME SAS</title>
        <style>
          * { box-sizing: border-box; margin: 0; padding: 0; }
          body {
            font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
            color: #1F2937;
            background: #FFFFFF;
            padding: 30px;
            font-size: 13px;
            line-height: 1.5;
          }
          .invoice-box {
            max-width: 850px;
            margin: 0 auto;
          }
          .header-table {
            width: 100%;
            border-collapse: collapse;
            margin-bottom: 24px;
            padding-bottom: 16px;
            border-bottom: 2px solid #10B981;
          }
          .logo-img {
            max-height: 52px;
            width: auto;
            margin-bottom: 6px;
          }
          .company-name {
            font-size: 20px;
            font-weight: 900;
            color: #111827;
            letter-spacing: -0.02em;
          }
          .company-info {
            font-size: 11px;
            color: #4B5563;
            line-height: 1.4;
          }
          .doc-badge-container {
            text-align: right;
          }
          .doc-title {
            font-size: 22px;
            font-weight: 900;
            color: #111827;
            text-transform: uppercase;
            letter-spacing: -0.02em;
          }
          .doc-number {
            font-size: 15px;
            font-weight: 800;
            color: #059669;
            margin-bottom: 6px;
          }
          .status-pill {
            display: inline-block;
            padding: 4px 12px;
            border-radius: 9999px;
            font-size: 11px;
            font-weight: 800;
            text-transform: uppercase;
            letter-spacing: 0.05em;
            background-color: ${statusBg};
            color: ${statusColor};
            border: 1px solid ${statusColor}40;
          }
          .info-grid {
            display: table;
            width: 100%;
            margin-bottom: 24px;
          }
          .info-col {
            display: table-cell;
            width: 50%;
            vertical-align: top;
          }
          .info-col:first-child {
            padding-right: 15px;
          }
          .info-col:last-child {
            padding-left: 15px;
          }
          .info-card {
            background-color: #F9FAFB;
            border: 1px solid #E5E7EB;
            border-radius: 8px;
            padding: 14px;
          }
          .info-card h4 {
            font-size: 11px;
            text-transform: uppercase;
            letter-spacing: 0.06em;
            color: #6B7280;
            font-weight: 700;
            margin-bottom: 8px;
            border-bottom: 1px solid #E5E7EB;
            padding-bottom: 4px;
          }
          .info-line {
            margin-bottom: 3px;
            font-size: 12px;
          }
          .items-table {
            width: 100%;
            border-collapse: collapse;
            margin-top: 10px;
            margin-bottom: 20px;
          }
          .items-table th {
            background-color: #111827;
            color: #FFFFFF;
            font-size: 11px;
            text-transform: uppercase;
            letter-spacing: 0.05em;
            padding: 9px 12px;
            font-weight: 700;
          }
          .items-table td {
            padding: 10px 12px;
            border-bottom: 1px solid #E5E7EB;
          }
          .totals-table {
            width: 320px;
            margin-left: auto;
            border-collapse: collapse;
            margin-bottom: 24px;
          }
          .totals-table td {
            padding: 6px 10px;
            font-size: 13px;
          }
          .totals-table tr.total-row td {
            border-top: 2px solid #111827;
            font-size: 15px;
            font-weight: 900;
            color: #111827;
            padding-top: 8px;
          }
          .payment-box {
            background-color: #ECFDF5;
            border: 1px solid #A7F3D0;
            border-radius: 8px;
            padding: 14px 18px;
            margin-top: 20px;
            font-size: 12px;
          }
          .footer {
            margin-top: 36px;
            padding-top: 16px;
            border-top: 1px solid #E5E7EB;
            text-align: center;
            font-size: 11px;
            color: #6B7280;
          }
          @media print {
            body { padding: 0; background: transparent; }
            .invoice-box { max-width: 100%; }
            @page {
              size: letter portrait;
              margin: 12mm 15mm;
            }
          }
        </style>
      </head>
      <body>
        <div class="invoice-box">
          <!-- ENCABEZADO -->
          <table class="header-table">
            <tr>
              <td style="width: 55%; vertical-align: top;">
                <img src="${baseUrl}/images/Logotipo-RX-big.png" alt="RedXtreme" class="logo-img" onerror="this.style.display='none'" />
                <div class="company-name">GRUPO DIGITALXTREME SAS</div>
                <div class="company-info">
                  <strong>NIT:</strong> 901312286<br>
                  <strong>Email:</strong> info@redxtreme.com · <strong>Tel / WhatsApp:</strong> +57 305 4395207<br>
                  <strong>Web:</strong> https://redxtreme.com · Medellín, Colombia
                </div>
              </td>
              <td style="width: 45%; vertical-align: top;" class="doc-badge-container">
                <div class="doc-title">CUENTA DE COBRO</div>
                <div class="doc-number">No. ${invoice.invoiceNumber || 'CC-DRAFT'}</div>
                <div style="margin-bottom: 8px;">
                  <span class="status-pill">${statusLabel}</span>
                </div>
                <div class="company-info" style="text-align: right;">
                  <strong>Fecha de Emisión:</strong> ${formatDate(invoice.issueDate || invoice.createdAt)}<br>
                  <strong>Fecha de Vencimiento:</strong> ${formatDate(invoice.dueDate)}<br>
                  <strong>Modalidad de Pago:</strong> ${planType}
                </div>
              </td>
            </tr>
          </table>

          <!-- DATOS CLIENTE Y PLAN -->
          <div class="info-grid">
            <div class="info-col">
              <div class="info-card">
                <h4>Datos del Cliente / Cobrar a:</h4>
                <div class="info-line"><strong>Nombre / Razón Social:</strong> ${client.name || 'Cliente Particular'}</div>
                ${client.documentNumber ? `<div class="info-line"><strong>${client.documentType || 'NIT/CC'}:</strong> ${client.documentNumber}</div>` : ''}
                ${client.contactPerson ? `<div class="info-line"><strong>Contacto:</strong> ${client.contactPerson}</div>` : ''}
                ${client.email ? `<div class="info-line"><strong>Correo:</strong> ${client.email}</div>` : ''}
                ${client.phone ? `<div class="info-line"><strong>Teléfono:</strong> ${client.phone}</div>` : ''}
                ${client.address ? `<div class="info-line"><strong>Dirección:</strong> ${client.address}</div>` : ''}
              </div>
            </div>
            <div class="info-col">
              <div class="info-card">
                <h4>Estado Financiero del Documento:</h4>
                <div class="info-line"><strong>Total Facturado:</strong> ${formatCOP(invoice.total)}</div>
                <div class="info-line" style="color: #059669;"><strong>Total Abonado:</strong> ${formatCOP(totalPaid)}</div>
                <div class="info-line" style="color: ${balanceDue > 0 ? '#DC2626' : '#059669'}; font-size: 13px;">
                  <strong>Saldo Pendiente:</strong> <strong>${formatCOP(balanceDue)}</strong>
                </div>
                <div class="info-line" style="margin-top: 6px; font-size: 11px; color: #6B7280;">
                  * Todo abono queda respaldado bajo la Cuenta de Cobro <strong>${invoice.invoiceNumber}</strong>.
                </div>
              </div>
            </div>
          </div>

          <!-- TABLA DE SERVICIOS -->
          <table class="items-table">
            <thead>
              <tr>
                <th style="width: 5%; text-align: center;">#</th>
                <th style="width: 50%; text-align: left;">Descripción del Servicio</th>
                <th style="width: 10%; text-align: center;">Cant.</th>
                <th style="width: 17%; text-align: right;">Valor Unitario</th>
                <th style="width: 18%; text-align: right;">Total</th>
              </tr>
            </thead>
            <tbody>
              ${itemsHtml}
            </tbody>
          </table>

          <!-- TOTALES -->
          <table class="totals-table">
            <tr>
              <td style="color: #6B7280;">Subtotal:</td>
              <td style="text-align: right; font-weight: 600;">${formatCOP(invoice.subtotal || invoice.total)}</td>
            </tr>
            ${invoice.discount > 0 ? `
              <tr>
                <td style="color: #059669;">Descuento Comercial:</td>
                <td style="text-align: right; font-weight: 600; color: #059669;">-${formatCOP(invoice.discount)}</td>
              </tr>
            ` : ''}
            <tr class="total-row">
              <td>Total a Pagar:</td>
              <td style="text-align: right;">${formatCOP(invoice.total)}</td>
            </tr>
          </table>

          <!-- ABONOS REGISTRADOS -->
          ${paymentsHtml}

          <!-- DATOS BANCARIOS Y CONDICIONES -->
          <div class="payment-box">
            <strong style="color: #065F46; display: block; margin-bottom: 6px; font-size: 12px; text-transform: uppercase; letter-spacing: 0.04em;">
              🏦 Información Bancaria para Pagos y Transferencias:
            </strong>
            <div style="color: #047857; line-height: 1.6; font-size: 12px;">
              • <strong>Entidad Bancaria:</strong> Bancolombia<br>
              • <strong>Tipo de Cuenta:</strong> Cuenta de Ahorros<br>
              • <strong>Número de Cuenta:</strong> 230 013619 02<br>
              • <strong>Titular:</strong> GRUPO DIGITALXTREME SAS · <strong>NIT:</strong> 901312286<br>
              • Enviar soporte de pago al WhatsApp <strong>+57 305 4395207</strong> o al correo <strong>facturacion@redxtreme.com</strong> citando el número de cuenta de cobro <strong>${invoice.invoiceNumber}</strong>.
            </div>
          </div>

          ${invoice.terms ? `
            <div style="margin-top: 16px; font-size: 11px; color: #6B7280; background: #F9FAFB; padding: 10px; border-radius: 6px; border: 1px solid #E5E7EB;">
              <strong>Términos y Condiciones:</strong> ${invoice.terms}
            </div>
          ` : ''}

          <!-- PIE DE PÁGINA -->
          <div class="footer">
            Gracias por confiar en <strong>GRUPO DIGITALXTREME SAS</strong>. Transformamos marcas en ecosistemas digitales de alto rendimiento.<br>
            Documento generado electrónicamente por <strong>RxApp Enterprise Platform</strong>.
          </div>
        </div>

        <script>
          setTimeout(() => {
            window.print();
          }, 600);
        </script>
      </body>
    </html>
  `);
  printWin.document.close();
};

/**
 * Genera e imprime el documento comercial oficial de una VENTA / ORDEN DE SERVICIO
 */
export const printSaleDocument = (sale) => {
  if (!sale) return;

  const printWin = window.open('', '_blank', 'width=900,height=750');
  if (!printWin) {
    alert('Por favor permite las ventanas emergentes (popups) para generar el documento de impresión.');
    return;
  }

  const baseUrl = window.location.origin;
  const client = sale.client || {};
  const totalAmount = sale.total || 0;
  const totalPaid = sale.invoice?.totalPaid || 0;
  const balanceDue = Math.max(0, totalAmount - totalPaid);

  const planType = sale.paymentPlan?.type === 'CUSTOM'
    ? 'Cuotas Personalizadas'
    : (sale.paymentPlan?.type || '100% Anticipado');

  const itemsHtml = (sale.items || []).map((item, idx) => `
    <tr>
      <td style="text-align: center; color: #6B7280; font-size: 12px;">${idx + 1}</td>
      <td>
        <strong style="color: #111827; font-size: 13px; display: block;">${item.service?.name || item.description || 'Servicio Profesional'}</strong>
        ${item.service?.description ? `<span style="font-size: 11px; color: #6B7280;">${item.service.description}</span>` : ''}
      </td>
      <td style="text-align: center; font-weight: 600; font-size: 13px;">${item.quantity || 1}</td>
      <td style="text-align: right; font-size: 13px;">${formatCOP(item.unitPrice)}</td>
      <td style="text-align: right; font-weight: 700; color: #111827; font-size: 13px;">${formatCOP(item.totalPrice)}</td>
    </tr>
  `).join('');

  printWin.document.write(`
    <!DOCTYPE html>
    <html lang="es">
      <head>
        <meta charset="UTF-8">
        <title>Orden de Venta #${sale.saleNumber || sale._id?.slice(-6)} - GRUPO DIGITALXTREME SAS</title>
        <style>
          * { box-sizing: border-box; margin: 0; padding: 0; }
          body {
            font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
            color: #1F2937;
            background: #FFFFFF;
            padding: 30px;
            font-size: 13px;
            line-height: 1.5;
          }
          .invoice-box { max-width: 850px; margin: 0 auto; }
          .header-table {
            width: 100%;
            border-collapse: collapse;
            margin-bottom: 24px;
            padding-bottom: 16px;
            border-bottom: 2px solid #2563EB;
          }
          .logo-img { max-height: 52px; width: auto; margin-bottom: 6px; }
          .company-name { font-size: 20px; font-weight: 900; color: #111827; letter-spacing: -0.02em; }
          .company-info { font-size: 11px; color: #4B5563; line-height: 1.4; }
          .doc-badge-container { text-align: right; }
          .doc-title { font-size: 22px; font-weight: 900; color: #111827; text-transform: uppercase; letter-spacing: -0.02em; }
          .doc-number { font-size: 15px; font-weight: 800; color: #2563EB; margin-bottom: 6px; }
          .status-pill {
            display: inline-block;
            padding: 4px 12px;
            border-radius: 9999px;
            font-size: 11px;
            font-weight: 800;
            text-transform: uppercase;
            background-color: #DBEAFE;
            color: #1E40AF;
            border: 1px solid #93C5FD;
          }
          .info-grid { display: table; width: 100%; margin-bottom: 24px; }
          .info-col { display: table-cell; width: 50%; vertical-align: top; }
          .info-col:first-child { padding-right: 15px; }
          .info-col:last-child { padding-left: 15px; }
          .info-card {
            background-color: #F9FAFB;
            border: 1px solid #E5E7EB;
            border-radius: 8px;
            padding: 14px;
          }
          .info-card h4 {
            font-size: 11px;
            text-transform: uppercase;
            letter-spacing: 0.06em;
            color: #6B7280;
            font-weight: 700;
            margin-bottom: 8px;
            border-bottom: 1px solid #E5E7EB;
            padding-bottom: 4px;
          }
          .info-line { margin-bottom: 3px; font-size: 12px; }
          .items-table {
            width: 100%;
            border-collapse: collapse;
            margin-top: 10px;
            margin-bottom: 20px;
          }
          .items-table th {
            background-color: #1E293B;
            color: #FFFFFF;
            font-size: 11px;
            text-transform: uppercase;
            letter-spacing: 0.05em;
            padding: 9px 12px;
            font-weight: 700;
          }
          .items-table td { padding: 10px 12px; border-bottom: 1px solid #E5E7EB; }
          .totals-table {
            width: 320px;
            margin-left: auto;
            border-collapse: collapse;
            margin-bottom: 24px;
          }
          .totals-table td { padding: 6px 10px; font-size: 13px; }
          .totals-table tr.total-row td {
            border-top: 2px solid #111827;
            font-size: 15px;
            font-weight: 900;
            color: #111827;
            padding-top: 8px;
          }
          .footer {
            margin-top: 36px;
            padding-top: 16px;
            border-top: 1px solid #E5E7EB;
            text-align: center;
            font-size: 11px;
            color: #6B7280;
          }
          @media print {
            body { padding: 0; background: transparent; }
            .invoice-box { max-width: 100%; }
            @page { size: letter portrait; margin: 12mm 15mm; }
          }
        </style>
      </head>
      <body>
        <div class="invoice-box">
          <!-- ENCABEZADO -->
          <table class="header-table">
            <tr>
              <td style="width: 55%; vertical-align: top;">
                <img src="${baseUrl}/images/Logotipo-RX-big.png" alt="RedXtreme" class="logo-img" onerror="this.style.display='none'" />
                <div class="company-name">GRUPO DIGITALXTREME SAS</div>
                <div class="company-info">
                  <strong>NIT:</strong> 901312286 · Soluciones Tecnológicas & Desarrollo Web<br>
                  <strong>Email:</strong> info@redxtreme.com · <strong>Tel:</strong> +57 305 4395207<br>
                  <strong>Web:</strong> https://redxtreme.com · Medellín, Colombia
                </div>
              </td>
              <td style="width: 45%; vertical-align: top;" class="doc-badge-container">
                <div class="doc-title">ORDEN DE VENTA</div>
                <div class="doc-number">#${sale.saleNumber || sale._id?.slice(-6)}</div>
                <div style="margin-bottom: 8px;">
                  <span class="status-pill">${sale.status === 'CONFIRMED' ? 'CONFIRMADA' : sale.status}</span>
                </div>
                <div class="company-info" style="text-align: right;">
                  <strong>Fecha de Venta:</strong> ${formatDate(sale.createdAt)}<br>
                  <strong>Modalidad Acordada:</strong> ${planType}
                </div>
              </td>
            </tr>
          </table>

          <!-- DATOS CLIENTE Y RESUMEN -->
          <div class="info-grid">
            <div class="info-col">
              <div class="info-card">
                <h4>Información del Cliente:</h4>
                <div class="info-line"><strong>Nombre / Empresa:</strong> ${client.name || 'Cliente Particular'}</div>
                ${client.contactPerson ? `<div class="info-line"><strong>Contacto:</strong> ${client.contactPerson}</div>` : ''}
                ${client.email ? `<div class="info-line"><strong>Correo:</strong> ${client.email}</div>` : ''}
                ${client.phone ? `<div class="info-line"><strong>Teléfono:</strong> ${client.phone}</div>` : ''}
                ${client.address ? `<div class="info-line"><strong>Ubicación:</strong> ${client.address}</div>` : ''}
              </div>
            </div>
            <div class="info-col">
              <div class="info-card">
                <h4>Condiciones Comerciales:</h4>
                <div class="info-line"><strong>Valor Total del Proyecto:</strong> ${formatCOP(totalAmount)}</div>
                <div class="info-line"><strong>Plan de Pago:</strong> ${planType}</div>
                <div class="info-line" style="color: #059669;"><strong>Abono Recibido:</strong> ${formatCOP(totalPaid)}</div>
                <div class="info-line" style="color: ${balanceDue > 0 ? '#DC2626' : '#059669'};">
                  <strong>Saldo por Facturar:</strong> ${formatCOP(balanceDue)}
                </div>
              </div>
            </div>
          </div>

          <!-- TABLA DE SERVICIOS -->
          <table class="items-table">
            <thead>
              <tr>
                <th style="width: 5%; text-align: center;">#</th>
                <th style="width: 50%; text-align: left;">Servicio / Módulo Incluido</th>
                <th style="width: 10%; text-align: center;">Cant.</th>
                <th style="width: 17%; text-align: right;">Valor Unitario</th>
                <th style="width: 18%; text-align: right;">Total</th>
              </tr>
            </thead>
            <tbody>
              ${itemsHtml}
            </tbody>
          </table>

          <!-- TOTALES -->
          <table class="totals-table">
            <tr>
              <td style="color: #6B7280;">Subtotal:</td>
              <td style="text-align: right; font-weight: 600;">${formatCOP(sale.subtotal || totalAmount)}</td>
            </tr>
            ${sale.discount > 0 ? `
              <tr>
                <td style="color: #059669;">Descuento Otorgado:</td>
                <td style="text-align: right; font-weight: 600; color: #059669;">-${formatCOP(sale.discount)}</td>
              </tr>
            ` : ''}
            <tr class="total-row">
              <td>Inversión Total:</td>
              <td style="text-align: right;">${formatCOP(totalAmount)}</td>
            </tr>
          </table>

          <!-- PIE DE PÁGINA -->
          <div class="footer">
            GRUPO DIGITALXTREME SAS · Transformación Digital, Software a la Medida y Agentes de Inteligencia Artificial.<br>
            Documento de control emitido por <strong>RxApp Platform</strong>.
          </div>
        </div>

        <script>
          setTimeout(() => {
            window.print();
          }, 600);
        </script>
      </body>
    </html>
  `);
  printWin.document.close();
};
